/**
 * Converts a SAN (Standard Algebraic Notation) chess move to a simple English description.
 * Handles disambiguation, captures, promotion, and check/checkmate in a simple, readable way.
 * If the main parse fails (e.g., destination is not a valid square), a helper attempts to parse file/rank disambiguation cases.
 * Examples:
 *   'e4'      => '(pawn) [to e4]'
 *   'Nxe4+'   => '[knight] [takes e4] [check]'
 *   'O-O'     => '[kingside castling]'
 *   'O-O-O#'  => '[queenside castling] [checkmate]'
 *   'Nbd7'    => '[knight] [from b-file] [to d7]'
 *   'R1e1'    => '[rook] [from rank 1] [to e1]'
 *   'Nb1d7'   => '[knight] [from b1] [to d7]'
 *
 * @param san - The SAN string to convert
 * @returns English description of the move
 */
export function sanToEnglish(san: string): string {
  const pieceMap: Record<string, string> = {
    'N': 'knight',
    'B': 'bishop',
    'R': 'rook',
    'Q': 'queen',
    'K': 'king',
  };

  // Helper to check if a string is a valid square
  const isSquare = (s: string) => /^[a-h][1-8]$/.test(s);

  // Handle castling
  if (/^O-O(-O)?[+#]?$/.test(san)) {
    let desc = san.startsWith('O-O-O') ? '[queenside castling]' : '[kingside castling]';
    if (san.endsWith('#')) desc += ' [checkmate]';
    else if (san.endsWith('+')) desc += ' [check]';
    return desc;
  }

  // Piece move: always if first char is NBRQK
  if (san[0] && pieceMap[san[0]]) {
    const piece = pieceMap[san[0]];
    let idx = 1;
    let disambig = '';
    // Full-square disambiguation: e.g., Re5e1
    if (
      isSquare(san.slice(idx, idx + 2)) &&
      isSquare(san.slice(idx + 2, idx + 4))
    ) {
      disambig = `[from ${san.slice(idx, idx + 2)}]`;
      idx += 2;
      // Capture
      let takes = false;
      if (san[idx] === 'x') {
        takes = true;
        idx += 1;
      }
      const dest = san.slice(idx, idx + 2);
      idx += 2;
      let promo = '';
      if (san[idx] === '=') {
        promo = `[promotes to ${pieceMap[san[idx + 1]?.toUpperCase()] || san[idx + 1]}]`;
        idx += 2;
      }
      let check = '';
      if (san[idx] === '+') check = '[check]';
      else if (san[idx] === '#') check = '[checkmate]';
      let desc = `[${piece}]`;
      if (disambig) desc += ' ' + disambig;
      if (takes) desc += ` [takes ${dest}]`;
      else desc += ` [to ${dest}]`;
      if (promo) desc += ' ' + promo;
      if (check) desc += ' ' + check;
      return desc.trim();
    }
    // Try to parse as normal piece move
    let takes = false;
    if (san[idx] === 'x') {
      takes = true;
      idx += 1;
    }
    const dest = san.slice(idx, idx + 2);
    idx += 2;
    let promo = '';
    if (san[idx] === '=') {
      promo = `[promotes to ${pieceMap[san[idx + 1]?.toUpperCase()] || san[idx + 1]}]`;
      idx += 2;
    }
    let check = '';
    if (san[idx] === '+') check = '[check]';
    else if (san[idx] === '#') check = '[checkmate]';
    // If destination is valid, return result
    if (isSquare(dest)) {
      let desc = `[${piece}]`;
      if (takes) desc += ` [takes ${dest}]`;
      else desc += ` [to ${dest}]`;
      if (promo) desc += ' ' + promo;
      if (check) desc += ' ' + check;
      return desc.trim();
    }
    // If not valid, try file/rank disambiguation helper
    return parseDisambiguationSAN(san, pieceMap, isSquare);
  }

  // Pawn move (if not a piece move)
  let idx = 0;
  let takes = false;
  if (/[a-h]/.test(san[idx]) && san[idx + 1] === 'x') {
    takes = true;
    idx += 2;
  }
  const dest = san.slice(idx, idx + 2);
  idx += 2;
  let promo = '';
  if (san[idx] === '=') {
    promo = `[promotes to ${pieceMap[san[idx + 1]?.toUpperCase()] || san[idx + 1]}]`;
    idx += 2;
  }
  let check = '';
  if (san[idx] === '+') check = '[check]';
  else if (san[idx] === '#') check = '[checkmate]';
  let desc = '(pawn)';
  if (takes) desc += ` [takes ${dest}]`;
  else desc += ` [to ${dest}]`;
  if (promo) desc += ' ' + promo;
  if (check) desc += ' ' + check;
  return desc.trim();
}

/**
 * Helper to parse SAN with file or rank disambiguation (not full-square)
 */
function parseDisambiguationSAN(san: string, pieceMap: Record<string, string>, isSquare: (s: string) => boolean): string {
  const piece = pieceMap[san[0]];
  let idx = 1;
  let disambig = '';
  // File disambiguation (e.g., Nbd7)
  if (/[a-h]/.test(san[idx])) {
    disambig = `[from ${san[idx]}-file]`;
    idx += 1;
  } else if (/[1-8]/.test(san[idx])) {
    // Rank disambiguation (e.g., N1d7)
    disambig = `[from rank ${san[idx]}]`;
    idx += 1;
  }
  // Capture
  let takes = false;
  if (san[idx] === 'x') {
    takes = true;
    idx += 1;
  }
  // Destination
  const dest = san.slice(idx, idx + 2);
  idx += 2;
  // Promotion
  let promo = '';
  if (san[idx] === '=') {
    promo = `[promotes to ${pieceMap[san[idx + 1]?.toUpperCase()] || san[idx + 1]}]`;
    idx += 2;
  }
  // Check/checkmate
  let check = '';
  if (san[idx] === '+') check = '[check]';
  else if (san[idx] === '#') check = '[checkmate]';
  // Build description
  let desc = `[${piece}]`;
  if (disambig) desc += ' ' + disambig;
  if (takes) desc += ` [takes ${dest}]`;
  else desc += ` [to ${dest}]`;
  if (promo) desc += ' ' + promo;
  if (check) desc += ' ' + check;
  return desc.trim();
} 